import React, {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Alert, Box, Container, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { VARIANT_ENUM } from "@kargo/shared-components.krg-text-input";
import Delete from "@mui/icons-material/Delete";
import KrgButton from "@kargo/shared-components.krg-button";

import { PATHS } from "../../../constants/paths";
import SinglePlaylistEditor from "./SinglePlaylistEditor";
import LoadingCircle from "../../../components/LoadingCircle";
import SinglePlaylistActionButtons from "./SinglePlaylistActionButtons";
import {
  Playlist,
  PlaylistCreationEnum,
  PlaylistCreationType,
} from "../../../@types/playlist.types";
import {
  submitForm,
  checkInitialFormValidation,
} from "../../../context/form/form.actions";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import { useFormContext } from "../../../context/form/form.provider";
import { FormActionsEnum, FormInitInputs } from "../../../@types/form.types";
import usePlaylist from "../../../hooks/usePlaylist";
import { Video } from "../../../api/types";
import SinglePlaylistMediaList from "./SinglePlaylistMediaList";
import useSnackbar from "../../../hooks/useSnackbar";
import { SidebarListItemButtonProps } from "../../../@types/sidebar.types";
import { handleBack } from "../../../helpers/utils";
import SidebarBackIconItem from "../../../components/Sidebar/SidebarBackIconItem";
import useSinglePageSidebar from "../../../hooks/useSinglePageSidebar";

type SinglePlaylistPageProps = {
  playlist: Playlist;
  token: string;
  creationType: PlaylistCreationType;
  initialFormState: FormInitInputs;
};

const SinglePlaylistPageContainer = ({
  token,
  initialFormState,
  creationType,
}: SinglePlaylistPageProps) => {
  const history = useHistory();
  const [
    { formInputFields, errors, data, showAllErrors, submitting, touched },
    dispatch,
  ] = useFormContext();
  const [showModal, setShowModal] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);
  const [hasMediaChanges, setHasMediaChanges] = useState(false);

  const queryClient = useQueryClient();
  const {
    getPlaylistMedias,
    isMediasLoading,
    mutations: { updateMutation, deleteMutation },
  } = usePlaylist(token);
  const { mutateAsync } = updateMutation;
  const [mediaList, setMediaList] = useState<Video[]>([]);
  const [initialMediaList, setInitialMediaList] = useState<Video[]>([]);
  const { snackbarSuccess } = useSnackbar();
  const resetMediaList = () => {
    setMediaList(initialMediaList);
    setHasMediaChanges(false);
  };
  const handleBackClick = useCallback(() => {
    if (touched || hasMediaChanges) {
      setShowBackModal(true);
    } else {
      handleBack(history, PATHS.MEDIA_GALLERY_PLAYLISTS);
    }
  }, [touched, hasMediaChanges, history]);
  // Show single page sidebar
  const sidebarItem: SidebarListItemButtonProps = {
    selected: false,
    text: "Media Gallery",
    onClick: handleBackClick,
    iconElement: <SidebarBackIconItem />,
  };
  useSinglePageSidebar([sidebarItem]);

  const handleAddVideos = (videos: Video[]) => {
    const newMediaList = [...mediaList, ...videos];
    setMediaList(newMediaList);
    setHasMediaChanges(true);
    snackbarSuccess(
      "Videos added successfully. Please save the playlist to update the media list.",
    );
  };

  const handleRemoveVideo = (mediaToken: string) => {
    setMediaList(mediaList.filter((media) => media.token !== mediaToken));
    setHasMediaChanges(true);
  };

  const handleReorderVideos = (oldIndex: number, newIndex: number) => {
    const reorderedMediaList = [...mediaList];
    const [removed] = reorderedMediaList.splice(oldIndex, 1);
    reorderedMediaList.splice(newIndex, 0, removed);
    setMediaList(reorderedMediaList);
    setHasMediaChanges(true);
  };

  const submitHandler = async (ev: FormEvent | MouseEvent) => {
    ev.preventDefault();
    const formData = {
      ...data,
      ...(creationType === PlaylistCreationEnum.CUSTOM && {
        medias: mediaList.map((media) => media.token),
      }),
    };

    submitForm(dispatch, errors, formData, mutateAsync);
  };

  useEffect(() => {
    getPlaylistMedias.refetch().then((resp) => {
      if (resp.isSuccess) {
        setMediaList(resp.data || []);
        setInitialMediaList(resp.data || []);
      }
    });
  }, []);

  // ✅ Reset mutation states to prevent stale success states
  useEffect(() => {
    updateMutation.reset();
    deleteMutation.reset();
  }, []);

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      queryClient.invalidateQueries(["playlists"]);
      queryClient
        .resetQueries(["playlist"])
        .then(() => history.push(PATHS.MEDIA_GALLERY_PLAYLISTS));
    }
  }, [deleteMutation.isSuccess]);

  useEffect(() => {
    if (updateMutation.isSuccess) {
      setInitialMediaList([...mediaList]);
      queryClient.invalidateQueries({ queryKey: ["playlist", token] });
    }
  }, [updateMutation.isSuccess, token, queryClient]);

  useEffect(() => {
    if (!touched && formInputFields) {
      checkInitialFormValidation(dispatch, formInputFields);
    }
  }, []);

  useEffect(() => {
    if (!submitting && !Object.keys(errors).length && hasMediaChanges) {
      setHasMediaChanges(false);
    }
  }, [submitting, errors]);
  return (
    <Container className="page-container">
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmitClick={() => deleteMutation.mutate(token)}
        header="Deleting Playlist"
        body="Are you sure you want to delete this playlist?"
        submitButtonText="Delete"
        cancelButtonText="Cancel"
      />

      {/* Back Navigation Confirm Modal */}
      <ConfirmModal
        isOpen={showBackModal}
        onClose={() => {
          setShowBackModal(false);
          handleBack(history, PATHS.MEDIA_GALLERY_PLAYLISTS);
        }}
        onSubmitClick={() => setShowBackModal(false)}
        header="Unsaved Changes"
        body="You have unsaved changes to this playlist. If you leave now, your changes will be lost. Would you like to continue editing or leave without saving?"
        submitButtonText="Continue Editing"
        cancelButtonText="Leave Page"
        isCloseIconDismissible={false}
      />

      <Box className="page-title-container">
        <Typography variant="h1" className="page-title">
          {formInputFields.title?.value?.toString()}
        </Typography>
      </Box>

      <SinglePlaylistActionButtons
        initialFormState={initialFormState}
        submitHandler={submitHandler}
        creationType={creationType}
        hasMediaChanges={hasMediaChanges}
        resetMediaList={resetMediaList}
      />

      {Object.keys(errors).length > 0 && showAllErrors && (
        <Alert
          severity="error"
          sx={{ marginBottom: "20px" }}
          onClose={() => dispatch({ type: FormActionsEnum.CLOSE_FORM_ERROR })}
        >
          {Object.keys(errors).map((errKey) =>
            errors[errKey].map((err: string) => (
              <Typography key={`alert-${err}`}>{err}</Typography>
            )),
          )}
        </Alert>
      )}

      <Stack>
        <Grid container spacing={4}>
          {/* Form Fields */}
          <Grid
            item
            xs={12}
            md={5}
            order={{ xs: 2, md: 1 }}
            sx={{ paddingLeft: 0 }}
          >
            {submitting ? (
              <LoadingCircle />
            ) : (
              <form onSubmit={submitHandler}>
                <SinglePlaylistEditor
                  creationType={creationType}
                  onVideosAdded={handleAddVideos}
                  mediaList={mediaList}
                />
              </form>
            )}
          </Grid>
          {/* Media List Fields */}
          <Grid
            item
            xs={12}
            md={7}
            order={{ xs: 1, md: 2 }}
            sx={{ marginBottom: "25px" }}
          >
            {submitting || isMediasLoading ? (
              <Box
                sx={{
                  height: "100%",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <LoadingCircle />
              </Box>
            ) : (
              <SinglePlaylistMediaList
                mediaList={mediaList}
                creationType={creationType}
                sortType={formInputFields.sortType?.value as string}
                onRemoveVideo={handleRemoveVideo}
                onReorderVideos={handleReorderVideos}
              />
            )}
          </Grid>
        </Grid>
      </Stack>
      <Box>
        <KrgButton
          id="delete-playlist"
          className="form-delete-button"
          text="Delete Playlist"
          onClick={() => setShowModal(true)}
          variant={VARIANT_ENUM.outlined}
          isSquaredBorder
          startIcon={<Delete />}
          style={{
            marginTop: "17px",
            border: "none",
            paddingLeft: 0,
          }}
        />
      </Box>
    </Container>
  );
};

export default SinglePlaylistPageContainer;
